import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSmartBack } from "../hooks/useSmartBack";
import { useData } from "../contexts/DataContext";
import { Header } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { fmtPhone } from "../lib/time";
import SourceBadge from "../components/SourceBadge";
import SendInvoiceDialog from "../components/SendInvoiceDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { 
  ArrowLeft, 
  Phone, 
  Mail, 
  MapPin, 
  Save, 
  Loader2,
  FolderKanban,
  Clock,
  Plus,
  Camera,
  Link,
  Share2,
  Copy,
  Receipt,
  Send,
  Link2,
  FileDown,
  Pencil,
  Trash2
} from "lucide-react";
import PhotoUpload from "../components/PhotoUpload";
import QuickInvoiceModal from "../components/QuickInvoiceModal";
import EmailStatusBadge from "../components/EmailStatusBadge";
import BackToProjectBanner from "../components/BackToProjectBanner";
import SmsConsentToggle from "../components/SmsConsentToggle";
import AutosaveStatus from "../components/AutosaveStatus";
import LastEditedLabel from "../components/LastEditedLabel";
import { useAutosave } from "../hooks/useAutosave";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { goBack } = useSmartBack();
  const { 
    updateContact, 
    dropdownOptions, 
    fetchDropdownOptions,
    projects,
    fetchProjects,
    fetchActivities,
    deleteContact
  } = useData();

  const [contact, setContact] = useState(null);
  const [activities, setActivities] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const [portalLinks, setPortalLinks] = useState(null);
  const [contactInvoices, setContactInvoices] = useState([]);
  const [contactInvoiceEmails, setContactInvoiceEmails] = useState({});
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null);
  const [sendDialog, setSendDialog] = useState({ open: false, invoiceId: "", invoiceNumber: "" });

  const serviceTypes = dropdownOptions.service_types || [];
  const leadSources = dropdownOptions.lead_sources || [];
  const classifications = dropdownOptions.classifications || [];

  const loadContact = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/contacts/${id}`);
      setContact(response.data);
      setFormData(response.data);
      // Load portal links
      try {
        const linkRes = await axios.get(`${API_URL}/public/portal/admin/link/${id}`);
        setPortalLinks(linkRes.data);
      } catch (e) { /* no portal links */ }
    } catch (error) {
      toast.error("Contact not found");
      navigate("/contacts");
    }
    setLoading(false);
  }, [id, navigate]);

  useEffect(() => {
    loadContact();
    fetchDropdownOptions();
    fetchProjects();
  }, [loadContact, fetchDropdownOptions, fetchProjects]);

  useEffect(() => {
    const loadActivities = async () => {
      const acts = await fetchActivities("contact", id);
      setActivities(acts);
    };
    if (id) loadActivities();
  }, [id, fetchActivities]);

  useEffect(() => {
    const loadPhotos = async () => {
      try {
        const res = await axios.get(`${API_URL}/uploads/entity/contact/${id}`);
        setPhotos(res.data);
      } catch (e) { /* no photos */ }
    };
    if (id) loadPhotos();
  }, [id]);

  const loadContactInvoices = useCallback(async () => {
    if (!id) return;
    try {
      const res = await axios.get(`${API_URL}/invoices?contact_id=${id}`);
      setContactInvoices(res.data || []);
    } catch { /* no invoices */ }
  }, [id]);

  useEffect(() => { loadContactInvoices(); }, [loadContactInvoices]);

  // Fetch email-status badges for the contact's invoices
  useEffect(() => {
    if (!contactInvoices.length) { setContactInvoiceEmails({}); return; }
    const ids = contactInvoices.map(i => i.id).join(",");
    axios.get(`${API_URL}/emails/status`, { params: { entity_type: "invoice", entity_ids: ids } })
      .then(res => setContactInvoiceEmails(res.data?.records || {}))
      .catch(() => setContactInvoiceEmails({}));
  }, [contactInvoices]);

  const contactProjects = projects.filter(p => p.contact_id === id);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateContact(id, formData);
      setContact(updated);
      toast.success("Contact updated!");
    } catch (error) {
      toast.error("Failed to update contact");
    }
    setSaving(false);
  };

  // Autosave — fires ~900ms after the last edit, no toast (silent success).
  // Skips the first hydration pass and any change that already matches the
  // last saved snapshot. Manual Save button still works for reassurance.
  const { status: autosaveStatus, savedAt, saveNow } = useAutosave(
    formData,
    async (value) => {
      const updated = await updateContact(id, value);
      // Keep local state in sync but avoid overwriting fields the user is
      // still typing into — only update fields the server changed
      // (server-derived timestamps, normalized phone, etc.).
      setContact((prev) => ({ ...(prev || {}), ...updated }));
    },
    { delay: 900, enabled: !loading && !!id },
  );

  // Fire an immediate save when the user leaves the page (nav away, tab
  // close, etc.) so nothing gets lost mid-edit.
  useEffect(() => {
    const flush = () => { saveNow(); };
    window.addEventListener("beforeunload", flush);
    return () => {
      window.removeEventListener("beforeunload", flush);
      // Route change flush
      flush();
    };
  }, [saveNow]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#0F4C81]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-28 sm:pb-0">
      <BackToProjectBanner />
      <Header
        title="Contact Details"
        subtitle={<LastEditedLabel updatedAt={contact?.updated_at} />}
      >
        <AutosaveStatus status={autosaveStatus} savedAt={savedAt} />
        <Button
          variant="outline"
          className="h-10 sm:h-12"
          onClick={() => goBack("/contacts")}
        >
          <ArrowLeft className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">Back</span>
        </Button>
        {portalLinks && (
          <Button
            variant="outline"
            className="h-10 sm:h-12 border-[#0F4C81] text-[#0F4C81] hover:bg-[#0F4C81]/5"
            onClick={async () => {
              const name = (formData.name || "there").split(" ")[0];
              const message = `Hey ${name}, here's your Tides Team Exteriors portal — view your quotes, pay invoices, and earn referral rewards.`;
              const url = portalLinks.portal_url;
              if (navigator.share) {
                try {
                  await navigator.share({ title: "Tides Team Exteriors — Customer Portal", text: message, url });
                  toast.success("Portal shared");
                  return;
                } catch { /* user cancelled — fall through to clipboard fallback below */ }
              }
              navigator.clipboard.writeText(`${message}\n${url}`);
              toast.success("Portal link + message copied!");
            }}
            data-testid="share-portal-btn"
          >
            <Share2 className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Share Portal</span>
          </Button>
        )}
        <Button
          className="bg-[#0F4C81] hover:bg-[#0D4270] h-10 sm:h-12 px-4 sm:px-6"
          onClick={handleSave}
          disabled={saving}
          data-testid="save-contact-btn"
        >
          {saving ? (
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
          ) : (
            <Save className="w-5 h-5 mr-2" />
          )}
          Save Changes
        </Button>
        <Button
          variant="outline"
          className="h-10 sm:h-12 border-red-300 text-red-600 hover:bg-red-50"
          data-testid="delete-contact-btn"
          onClick={async () => {
            if (!window.confirm(`Delete ${contact?.name || "this contact"}? They'll be moved to Trash and can be restored from there.`)) return;
            try {
              await deleteContact(id);
              toast.success("Contact moved to Trash");
              goBack("/contacts");
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Failed to delete contact");
            }
          }}
        >
          <Trash2 className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">Delete</span>
        </Button>
      </Header>

      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 flex-wrap">
                  Contact Information
                  <SourceBadge source={formData.lead_source} />
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    value={formData.name || ""}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="h-12"
                    data-testid="edit-contact-name"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        value={formData.phone || ""}
                        onChange={(e) => setFormData({ ...formData, phone: fmtPhone(e.target.value) })}
                        className="h-12 pl-10"
                        data-testid="edit-contact-phone"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="email"
                        value={formData.email || ""}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="h-12 pl-10"
                        data-testid="edit-contact-email"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Street Address</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={formData.address || ""}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="123 Main Street"
                      className="h-12 pl-10"
                      data-testid="edit-contact-address"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input
                      value={formData.city || ""}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="Charleston"
                      className="h-12"
                      data-testid="edit-contact-city"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Input
                      value={formData.state || ""}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      placeholder="SC"
                      className="h-12"
                      data-testid="edit-contact-state"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Zip</Label>
                    <Input
                      value={formData.zip || ""}
                      onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                      placeholder="29401"
                      className="h-12"
                      data-testid="edit-contact-zip"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Service Type</Label>
                    <Select 
                      value={formData.service_type || ""} 
                      onValueChange={(v) => setFormData({ ...formData, service_type: v })}
                    >
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {serviceTypes.map(s => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Lead Source</Label>
                    <Select 
                      value={formData.lead_source || ""} 
                      onValueChange={(v) => setFormData({ ...formData, lead_source: v })}
                    >
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {leadSources.map(s => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Classification</Label>
                    <Select 
                      value={formData.classification || ""} 
                      onValueChange={(v) => setFormData({ ...formData, classification: v })}
                    >
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {classifications.map(c => (
                          <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <SmsConsentToggle
                  contact={formData}
                  onUpdated={(updated) => {
                    setContact(updated);
                    setFormData((prev) => ({ ...prev, sms_consent: updated.sms_consent, sms_consent_timestamp: updated.sms_consent_timestamp, sms_consent_source: updated.sms_consent_source }));
                  }}
                />

                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={formData.notes || ""}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={4}
                    data-testid="edit-contact-notes"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Projects Tab */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <FolderKanban className="w-5 h-5" />
                    Projects ({contactProjects.length})
                  </CardTitle>
                  <Button
                    size="sm"
                    className="bg-[#0F4C81] hover:bg-[#0D4270]"
                    onClick={() => navigate(`/projects?create=true&contact=${id}`)}
                    data-testid="create-project-for-contact"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    New Project
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {contactProjects.length > 0 ? (
                  <div className="space-y-3">
                    {contactProjects.map(project => (
                      <div 
                        key={project.id}
                        className="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                        onClick={() => navigate(`/projects/${project.id}`, { state: { from: `/contacts/${id}` } })}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-slate-700">
                              {project.project_address || "No address"}
                            </p>
                            <p className="text-sm text-slate-500">
                              {serviceTypes.find(s => s.value === project.service_type)?.label || project.service_type}
                            </p>
                          </div>
                          <Badge>{project.stage?.replace(/_/g, ' ')}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center py-8 text-slate-500">No projects yet</p>
                )}
              </CardContent>
            </Card>
            {/* Invoices */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Receipt className="w-5 h-5" />
                    Invoices ({contactInvoices.length})
                  </CardTitle>
                  <Button
                    size="sm"
                    className="bg-[#0F4C81] hover:bg-[#0D4270]"
                    onClick={() => setShowInvoiceModal(true)}
                    data-testid="contact-new-invoice-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    New Invoice
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {contactInvoices.length > 0 ? (
                  <div className="divide-y divide-slate-100">
                    {contactInvoices.map(inv => {
                      const isStandalone = !inv.project_id;
                      const amount = parseFloat(inv.amount) || 0;
                      const paid = parseFloat(inv.paid_amount) || 0;
                      const remaining = Math.max(amount - paid, 0);
                      const statusStyle = inv.status === "paid" ? "bg-emerald-100 text-emerald-700"
                        : inv.status === "partial" ? "bg-amber-100 text-amber-700"
                        : inv.status === "sent" ? "bg-blue-100 text-blue-700"
                        : "bg-slate-100 text-slate-600";
                      return (
                        <div
                          key={inv.id}
                          className="group flex items-center gap-3 py-3 cursor-pointer hover:bg-slate-50 -mx-2 px-2 rounded"
                          onClick={() => {
                            if (inv.project_id) navigate(`/projects/${inv.project_id}?tab=billing&invoice=${inv.id}`);
                            else navigate("/billing");
                          }}
                          data-testid={`contact-invoice-${inv.id}`}
                        >
                          <Receipt className={`w-4 h-4 flex-shrink-0 ${isStandalone ? "text-amber-600" : "text-[#0F4C81]"}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-sm text-slate-800">{inv.invoice_number || "Draft"}</span>
                              {isStandalone && <Badge className="bg-amber-100 text-amber-700 text-[10px] border-0">Quick</Badge>}
                              {inv.title && <span className="text-xs text-slate-500 truncate">{inv.title}</span>}
                            </div>
                            <p className="text-[11px] text-slate-400 mt-0.5">{inv.created_at ? new Date(inv.created_at).toLocaleDateString() : ""}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-sm text-slate-800">${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                            {paid > 0 && paid < amount && (
                              <p className="text-[10px] text-amber-600">${remaining.toFixed(2)} due</p>
                            )}
                          </div>
                          <Badge className={`${statusStyle} text-[10px] capitalize border-0`}>{inv.status}</Badge>
                          <EmailStatusBadge record={contactInvoiceEmails[inv.id]} />
                          <div className="flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                            {isStandalone && (
                              <button
                                type="button"
                                onClick={() => { setEditInvoice(inv); setShowInvoiceModal(true); }}
                                className="p-1.5 rounded hover:bg-[#0F4C81]/10 text-[#0F4C81]"
                                title="Edit invoice"
                                data-testid={`contact-invoice-edit-${inv.id}`}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {contact?.email && (
                              <button
                                type="button"
                                onClick={() => setSendDialog({
                                  open: true,
                                  invoiceId: inv.id,
                                  invoiceNumber: inv.invoice_number || "",
                                })}
                                className="p-1.5 rounded hover:bg-blue-100 text-blue-600"
                                title={`Email invoice to ${contact.email}`}
                                data-testid={`contact-invoice-send-${inv.id}`}
                              >
                                <Send className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                const code = inv.short_code || inv.id;
                                const link = `${window.location.origin}/i/${code}`;
                                navigator.clipboard.writeText(link);
                                toast.success("Payment link copied!");
                              }}
                              className="p-1.5 rounded hover:bg-emerald-100 text-emerald-600"
                              title="Copy payment link"
                              data-testid={`contact-invoice-copylink-${inv.id}`}
                            >
                              <Link2 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  const res = await axios.get(`${API_URL}/invoices/${inv.id}/pdf`, { responseType: "blob" });
                                  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                                  const a = document.createElement("a");
                                  a.href = url;
                                  a.setAttribute("download", `Invoice-${inv.invoice_number || inv.id}.pdf`);
                                  document.body.appendChild(a); a.click(); a.remove();
                                  setTimeout(() => window.URL.revokeObjectURL(url), 5000);
                                  toast.success("PDF downloaded!");
                                } catch (err) {
                                  console.error("[Invoice PDF] Download failed:", err);
                                  toast.error(err?.response?.status ? `Failed to generate PDF (${err.response.status})` : "Failed to generate PDF");
                                }
                              }}
                              className="p-1.5 rounded hover:bg-slate-200 text-slate-600"
                              title="Download PDF"
                              data-testid={`contact-invoice-pdf-${inv.id}`}
                            >
                              <FileDown className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-center py-8 text-slate-500 text-sm">No invoices yet. Create a quick invoice without a project.</p>
                )}
              </CardContent>
            </Card>
            {/* Photos */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Camera className="w-5 h-5" />
                  Photos & Files
                </CardTitle>
              </CardHeader>
              <CardContent>
                <PhotoUpload
                  entityType="contact"
                  entityId={id}
                  photos={photos}
                  onPhotosChange={setPhotos}
                />
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Quick Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Created</span>
                  <span className="font-medium">
                    {contact?.created_at ? new Date(contact.created_at).toLocaleDateString() : "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Projects</span>
                  <span className="font-medium">{contactProjects.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Classification</span>
                  <Badge variant="outline" className="capitalize">
                    {contact?.classification || "N/A"}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Activity Timeline */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Activity Timeline
                </CardTitle>
              </CardHeader>
              <CardContent>
                {activities.length > 0 ? (
                  <div className="space-y-4">
                    {activities.slice(0, 10).map(activity => (
                      <div key={activity.id} className="flex gap-3">
                        <div className="w-2 h-2 rounded-full bg-[#00A3E0] mt-2 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm text-slate-700">{activity.details}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            {new Date(activity.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center py-4 text-slate-500 text-sm">No activity yet</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <QuickInvoiceModal
        open={showInvoiceModal}
        onOpenChange={(v) => { setShowInvoiceModal(v); if (!v) setEditInvoice(null); }}
        contactId={id}
        editInvoice={editInvoice}
        onCreated={() => loadContactInvoices()}
      />
      <SendInvoiceDialog
        open={sendDialog.open}
        onOpenChange={(v) => setSendDialog(prev => ({ ...prev, open: v }))}
        invoiceId={sendDialog.invoiceId}
        invoiceNumber={sendDialog.invoiceNumber}
        defaultEmail={contact?.email || ""}
        onSent={loadContactInvoices}
      />

      {/* Mobile-only fixed save bar — guarantees Save is always one tap
          away on mobile, where the header Save button can be missed. */}
      <div className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 px-4 py-3 flex items-center gap-2 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] sm:hidden" data-testid="mobile-save-bar">
        <Button
          className="flex-1 h-12 text-base font-semibold bg-[#0F4C81] hover:bg-[#0D4270]"
          onClick={handleSave}
          disabled={saving}
          data-testid="mobile-save-contact-btn"
        >
          {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Save className="w-5 h-5 mr-2" />}
          {saving ? "Saving…" : "Save Changes"}
        </Button>
        <Button
          variant="outline"
          className="h-12 w-12 p-0 border-red-300 text-red-600 hover:bg-red-50"
          aria-label="Delete contact"
          data-testid="mobile-delete-contact-btn"
          onClick={async () => {
            if (!window.confirm("Delete this contact? It will be moved to Trash and can be restored from there.")) return;
            try {
              await deleteContact(id);
              toast.success("Contact moved to Trash");
              goBack("/contacts");
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Failed to delete contact");
            }
          }}
        >
          <Trash2 className="w-5 h-5" />
        </Button>
      </div>
    </div>
  );
}


